/**
 * Vocabolario unico dei materiali.
 *
 * Perche' esiste. La stessa informazione era descritta con QUATTRO vocabolari
 * incompatibili: il menu della scheda materiale (`MaterialFormData.ts`), i chip
 * a regex del picker (`MaterialPickerDialog.tsx`), il vecchio select v1
 * (`GroupedMaterialSelect.tsx`) e i valori scritti dagli import
 * (`saintGobainParser.ts`). Dei 25 valori di `board_typology` presenti sul
 * database, solo 2 esistevano nel menu: l'85% delle lastre, aperto in modifica,
 * mostrava il campo vuoto.
 *
 * COME FUNZIONA. Ogni valore canonico porta con se' una lista di ALIAS che
 * assorbe i valori storici. Gli alias risolvono in LETTURA: il catalogo non si
 * riscrive. E' una scelta deliberata —
 *  - `wallModel.ts` sceglie le texture della sezione con le sue regex sui
 *    valori grezzi, che restano intatti sul database;
 *  - `dedupByFormat.ts` usa i valori grezzi nella chiave canonica;
 *  - gli import continuano a scrivere `hydro_H1` e l'alias lo assorbe.
 *
 * Aggiungendo un valore nuovo, aggiungerlo QUI e in nessun altro posto.
 */

export interface TaxonomyOption {
  /** Valore canonico: quello che scrivono i materiali nuovi. */
  value: string;
  label: string;
  /** Valori storici che risolvono a questo canonico. */
  aliases?: string[];
}

/** Confronto insensibile a maiuscole, spazi, trattini e underscore. */
function normalizeKey(raw: string): string {
  return raw
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/**
 * Risolve un valore grezzo al suo canonico, o `null` se non riconosciuto.
 * Prima cerca la corrispondenza esatta, poi prova gli alias come sottostringa
 * (serve per i valori scritti a frase, es. "Zn-Mg (zincatura magnesiata...)").
 */
export function resolve(
  rawValue: string | null | undefined,
  taxonomy: TaxonomyOption[],
): string | null {
  if (!rawValue) return null;
  const key = normalizeKey(rawValue);
  if (!key) return null;

  for (const opt of taxonomy) {
    if (normalizeKey(opt.value) === key) return opt.value;
    if (opt.aliases?.some(a => normalizeKey(a) === key)) return opt.value;
  }
  for (const opt of taxonomy) {
    if (opt.aliases?.some(a => key.includes(normalizeKey(a)))) return opt.value;
  }
  return null;
}

/**
 * Opzioni da mostrare in un menu, tenendo conto del valore gia' salvato.
 *
 * Se il materiale porta un valore storico che non e' fra i canonici (es.
 * `hydro_H1`), viene aggiunta un'opzione con QUEL valore e l'etichetta
 * canonica ("Idrofuga"). Cosi' il menu dice la cosa giusta e, se l'utente non
 * tocca il campo, il valore originale si salva identico.
 *
 * E' il motivo per cui non serve riscrivere il catalogo: senza questa opzione
 * il menu mostrerebbe il vuoto sull'85% delle lastre, oppure — mostrando il
 * canonico — riscriverebbe `acoustic` in `acustica` al primo salvataggio,
 * facendo mancare la regex di `wallModel.ts` che cerca `acoust`.
 */
export function optionsWithCurrent(
  taxonomy: TaxonomyOption[],
  currentRaw: string | null | undefined,
): TaxonomyOption[] {
  if (!currentRaw) return taxonomy;
  if (taxonomy.some(o => o.value === currentRaw)) return taxonomy;

  const canonical = resolve(currentRaw, taxonomy);
  const known = taxonomy.find(o => o.value === canonical);
  return [
    ...taxonomy,
    {
      value: currentRaw,
      label: known ? `${known.label} (${currentRaw})` : `Non classificato (${currentRaw})`,
    },
  ];
}

/** Etichetta leggibile di un valore grezzo, con ripiego sul valore stesso. */
export function labelOf(
  rawValue: string | null | undefined,
  taxonomy: TaxonomyOption[],
): string {
  const canonical = resolve(rawValue, taxonomy);
  const opt = taxonomy.find(o => o.value === canonical);
  return opt?.label ?? (rawValue ?? '');
}

// ===========================================================================
// LASTRE
// ===========================================================================

/**
 * Linea commerciale della lastra — valore SINGOLO, quello della scheda materiale.
 * Per filtrare si usano invece le etichette derivate di `boardTags`, perche' una
 * lastra ha piu' proprieta' insieme (una Habito Hydro e' alta densita' E idrofuga).
 */
export const BOARD_TYPOLOGY: TaxonomyOption[] = [
  { value: 'standard', label: 'Standard', aliases: ['easy_posa'] },
  { value: 'idro', label: 'Idrofuga', aliases: ['hydro_H1', 'habito_hydro', 'idro_fire', 'H1'] },
  { value: 'ignifuga', label: 'Ignifuga', aliases: ['fireline_DF', 'DF'] },
  { value: 'alta_densita', label: 'Alta densità', aliases: ['habito', 'habito_forte_DFIR', 'high_density_activair', 'diamant'] },
  { value: 'doppia_densita', label: 'Doppia densità' },
  { value: 'acustica', label: 'Acustica', aliases: ['acoustic'] },
  { value: 'forata_acustica', label: 'Forata fonoassorbente', aliases: ['lastra_forata_acustica', 'gyptone', 'rigitone'] },
  { value: 'accoppiata', label: 'Accoppiata isolante', aliases: ['accoppiata_XPS'] },
  { value: 'termica', label: 'Termoisolante', aliases: ['thermoboard'] },
  { value: 'cementizia', label: 'Cementizia', aliases: ['cement_board', 'cementizia_aquaroc', 'glasroc_x', 'fibrocemento'] },
  { value: 'incombustibile', label: 'Incombustibile A1', aliases: ['A1_incombustibile'] },
  { value: 'fibrogesso', label: 'Fibrogesso', aliases: ['lignum'] },
  { value: 'flessibile', label: 'Flessibile', aliases: ['flex'] },
  { value: 'antivapore', label: 'Antivapore', aliases: ['vapor', 'habito_vapor'] },
  { value: 'speciale', label: 'Speciale', aliases: ['x_ray_protection', 'speciali'] },
];

/** Etichette con cui si FILTRA una lastra. Una lastra puo' portarne piu' d'una. */
export type BoardTag =
  | 'standard' | 'idro' | 'ignifuga' | 'alta_densita' | 'durezza'
  | 'esterno' | 'acustica' | 'forata' | 'cementizia' | 'incombustibile'
  | 'accoppiata' | 'termica';

export const BOARD_TAG_LABELS: Record<BoardTag, string> = {
  standard: 'Standard',
  idro: 'Idrofuga',
  ignifuga: 'Ignifuga',
  alta_densita: 'Alta densità',
  durezza: 'Alta durezza',
  esterno: 'Per esterni',
  acustica: 'Acustica',
  forata: 'Forata',
  cementizia: 'Cementizia',
  incombustibile: 'Incombustibile A1',
  accoppiata: 'Accoppiata',
  termica: 'Termoisolante',
};

/** Ordine di presentazione dei chip: prima cio' che si cerca piu' spesso. */
export const BOARD_TAG_ORDER: BoardTag[] = [
  'standard', 'idro', 'ignifuga', 'alta_densita', 'acustica', 'forata',
  'accoppiata', 'termica', 'cementizia', 'incombustibile', 'esterno', 'durezza',
];

interface BoardLike {
  en_520_type?: string | null;
  board_typology?: string | null;
  material_type?: string | null;
  name?: string | null;
  fire_class?: string | null;
}

/**
 * Proprieta' di una lastra, derivate e non digitate.
 *
 * La sorgente principale e' `en_520_type`, il codice UNI EN 520: e' normativo,
 * e' popolato su 503 lastre su 523 e descrive le proprieta' IN COMBINAZIONE.
 * "DEFH1IR" significa densita' + esterno + fuoco + idrofuga + durezza: un campo
 * a valore singolo ne perderebbe quattro quinti.
 *
 * Significato delle lettere:
 *   D  densita' maggiorata      E  per esterni
 *   F  nucleo migliorato al fuoco   H1/H2/H3  assorbimento d'acqua ridotto
 *   I  durezza superficiale      R  resistenza a flessione maggiorata
 *   A  lastra standard
 */
/**
 * Estrae le sole lettere di codice EN 520, scartando la prosa.
 *
 * Serve perche' il campo contiene anche testo libero: "DFIR + barriera vapore",
 * "D + Ba (baritata)", "D, I, R + barriera vapore". Leggendo le lettere alla
 * cieca, la E di "vaporE" darebbe "per esterni" e la I di "barItata" darebbe
 * "alta durezza" — due proprieta' inventate su lastre che non le hanno.
 *
 * Regola: si separa su virgole e "+", si tengono solo i pezzi scritti tutti in
 * MAIUSCOLO (che e' come si scrivono i codici) e si scarta il resto. Cosi'
 * "DFIR + H1" conserva entrambi, mentre "D + Ba (baritata)" si riduce a "D".
 */
function en520Letters(raw: string): string {
  if (!raw) return '';
  return raw
    .split(/[,+]/)
    .map(part => part.trim())
    .filter(part => part.length > 0 && part === part.toUpperCase())
    .join('')
    .replace(/[^A-Z0-9]/g, '');
}

export function boardTags(m: BoardLike | null | undefined): Set<BoardTag> {
  const tags = new Set<BoardTag>();
  if (!m) return tags;

  const en = String(m.en_520_type ?? '');
  const letters = en520Letters(en);

  // "A1" e' l'euroclasse di reazione al fuoco, non una lettera EN 520:
  // va intercettata PRIMA di leggere la "A" come "lastra standard".
  const isA1 = /\bA1\b/.test(en) || letters.startsWith('A1');

  if (isA1) tags.add('incombustibile');
  if (/H[123]/.test(letters)) tags.add('idro');
  if (letters.includes('F')) tags.add('ignifuga');
  if (letters.includes('D')) tags.add('alta_densita');
  if (letters.includes('I')) tags.add('durezza');
  if (letters.includes('E')) tags.add('esterno');

  // La linea commerciale aggiunge cio' che la norma non codifica
  // (fonoassorbenza, accoppiati, cementizie) e copre le 20 lastre senza EN 520.
  const canonical = resolve(m.board_typology, BOARD_TYPOLOGY);
  switch (canonical) {
    case 'standard': tags.add('standard'); break;
    case 'idro': tags.add('idro'); break;
    case 'ignifuga': tags.add('ignifuga'); break;
    case 'alta_densita':
    case 'doppia_densita': tags.add('alta_densita'); break;
    case 'acustica': tags.add('acustica'); break;
    case 'forata_acustica': tags.add('forata'); tags.add('acustica'); break;
    case 'accoppiata': tags.add('accoppiata'); break;
    case 'termica': tags.add('termica'); tags.add('accoppiata'); break;
    case 'cementizia': tags.add('cementizia'); break;
    case 'incombustibile': tags.add('incombustibile'); break;
    default: break;
  }

  // Le forate si riconoscono anche dal nome commerciale (Gyptone, Rigitone).
  if (/forat|gyptone|rigitone/i.test(`${m.board_typology ?? ''} ${m.material_type ?? ''} ${m.name ?? ''}`)) {
    tags.add('forata');
  }

  // Una lastra senza nessuna proprieta' particolare e' una standard.
  if (tags.size === 0 && letters.startsWith('A')) tags.add('standard');

  return tags;
}

// ===========================================================================
// ISOLANTI
// ===========================================================================

/**
 * NB: "lana minerale" NON compare, ed e' voluto: e' la FAMIGLIA che comprende
 * sia la lana di vetro sia quella di roccia, non un terzo tipo. I valori storici
 * `lana_minerale` e `pannello_lana_minerale` sono ambigui e vanno risolti per
 * prodotto (vedi il backfill), non con un alias: dei 22 `pannello_lana_minerale`
 * ben 16 hanno "roccia" nel nome.
 */
export const INSULATION_TYPE: TaxonomyOption[] = [
  { value: 'lana_vetro', label: 'Lana di vetro', aliases: ['pannello_lana_vetro'] },
  { value: 'lana_roccia', label: 'Lana di roccia', aliases: ['pannello_lana_roccia'] },
  { value: 'eps', label: 'EPS (polistirene espanso)', aliases: ['pannello_EPS'] },
  { value: 'xps', label: 'XPS (polistirene estruso)', aliases: ['pannello_XPS'] },
  { value: 'altro', label: 'Altro', aliases: ['pannello_isolante', 'isolante_sfuso'] },
];

// ===========================================================================
// PROFILI — rivestimento, lamierino, larghezza
// ===========================================================================

/**
 * ORDINE SIGNIFICATIVO: `zinco_magnesio` va PRIMA di `zincato`.
 * Il ripiego per sottostringa scorre la lista dall'alto, e il valore reale
 * "Zn-Mg (zincatura magnesiata anti-corrosione)" contiene la parola "zincatura":
 * con l'ordine invertito i 14 profili Zn-Mg finirebbero fra gli zincati normali.
 * Per lo stesso motivo niente alias di due lettere come "zn": troppo generici.
 */
export const PROFILE_COATING: TaxonomyOption[] = [
  { value: 'zinco_magnesio', label: 'Zinco-Magnesio', aliases: ['zn_mg', 'mgz', 'zincatura_magnesiata'] },
  { value: 'zincato', label: 'Zincato', aliases: ['zincatura_z140', 'z140'] },
  { value: 'verniciato', label: 'Verniciato', aliases: ['verniciatura', 'metalframe'] },
];

interface ProfileLike {
  surface_finish?: string | null;
  name?: string | null;
}

/**
 * Rivestimento di un profilo, o `null` se non determinabile.
 *
 * `surface_finish` e' vuoto su 608 profili su 652, ma 157 di quelli vuoti hanno
 * lo Zinco-Magnesio scritto NEL NOME. Leggerlo anche da li' rende il filtro utile
 * subito, senza aspettare il completamento dei dati.
 *
 * Se non si sa, si torna `null`: NON si assume "zincato". Un profilo Zn-Mg
 * classificato per sbaglio come zincato normale sparirebbe dal filtro giusto.
 */
export function profileCoating(m: ProfileLike | null | undefined): string | null {
  if (!m) return null;

  // Il NOME ha la precedenza sul campo quando dichiara lo Zinco-Magnesio.
  // E' un prodotto premium: chi lo vende lo scrive sempre nel nome, mentre il
  // campo `surface_finish` e' vuoto su 608 profili su 652 ed e' stato riempito
  // a caso in passato con un default 'zincatura_z140'. Fidarsi prima del campo
  // farebbe passare per zincato normale un profilo che si chiama "Zinco-Magnesio".
  const name = m.name ?? '';
  if (/zinco[\s-]?magnesio|\bzn[\s-]?mg\b|\bmgz\b|\bzm\b/i.test(name)) return 'zinco_magnesio';

  const fromField = resolve(m.surface_finish, PROFILE_COATING);
  if (fromField) return fromField;

  if (/verniciat|metalframe|antracite/i.test(name)) return 'verniciato';
  return null;
}

/**
 * Spessore del lamierino. Il database lo tiene in mm, il cantiere parla in decimi:
 * 0,6 mm = "6/10". Si mostra come lo si dice.
 */
export const SHEET_THICKNESS_OPTIONS: { value: number; label: string }[] = [
  { value: 0.4, label: '4/10' },
  { value: 0.5, label: '5/10' },
  { value: 0.6, label: '6/10' },
  { value: 0.7, label: '7/10' },
  { value: 0.8, label: '8/10' },
  { value: 1.0, label: '10/10' },
  { value: 1.5, label: '15/10' },
  { value: 2.0, label: '20/10' },
];

export function sheetThicknessLabel(mm: number | null | undefined): string | null {
  const v = Number(mm);
  if (!Number.isFinite(v) || v <= 0) return null;
  const known = SHEET_THICKNESS_OPTIONS.find(o => Math.abs(o.value - v) < 0.001);
  if (known) return known.label;
  return `${Math.round(v * 10)}/10`;
}

/** Larghezze nominali usate dai filtri. Tutto il resto finisce sotto "Altre". */
export const NOMINAL_WIDTHS = [15, 30, 50, 75, 100, 125, 150] as const;

/** Scarto ammesso fra larghezza reale e nominale: la guida e' 1-2 mm piu' larga. */
export const WIDTH_TOLERANCE_MM = 2;

/**
 * Bucket di larghezza per i FILTRI: 48, 49 e 50 stanno tutti sotto "50".
 * Torna `null` per le misure fuori elenco (24, 40, 55, 60...), che l'interfaccia
 * raggruppa sotto "Altre".
 *
 * ATTENZIONE: non usare questa funzione per accoppiare montante e guida. Un
 * montante da 60 e una guida da 40 tornano entrambi `null` e risulterebbero
 * compatibili. Per quello c'e' `widthsMatch`.
 */
export function nominalWidth(mm: number | null | undefined): number | null {
  const w = Number(mm);
  if (!Number.isFinite(w) || w <= 0) return null;
  for (const n of NOMINAL_WIDTHS) {
    if (Math.abs(w - n) <= WIDTH_TOLERANCE_MM) return n;
  }
  return null;
}

// ===========================================================================
// CAMPI OBBLIGATORI
// ===========================================================================

export interface RequiredField {
  /** Nome del campo in MaterialFormData. */
  field: string;
  label: string;
}

/**
 * Campi di classificazione senza i quali un materiale NUOVO non e' filtrabile.
 *
 * Si applica solo in CREAZIONE. Sui materiali gia' esistenti resta un avviso:
 * bloccare il salvataggio costringerebbe a compilare tre campi anche solo per
 * correggere un prezzo, e oggi sarebbero 152 profili senza lamierino, 104 senza
 * larghezza e 102 isolanti senza tipo.
 *
 * NB: `required` dell'HTML qui non serve a niente — sui <Select> di Radix il
 * trigger e' un <button>, e sui <select> nativi senza opzione vuota non scatta
 * mai. Per questo il controllo e' esplicito.
 */
export function requiredFieldsFor(category: string): RequiredField[] {
  switch (category) {
    case 'structure_frame':
    case 'structure_guide':
      return [
        { field: 'width', label: 'Larghezza (schiena)' },
        { field: 'sheet_thickness', label: 'Spessore lamierino' },
        { field: 'surface_finish', label: 'Rivestimento' },
      ];
    case 'insulation':
      return [{ field: 'material_type', label: 'Tipo di materiale' }];
    case 'board':
    case 'ceiling_tile':
      return [{ field: 'board_typology', label: 'Tipologia' }];
    default:
      return [];
  }
}

/** I campi obbligatori non compilati, per la categoria del materiale. */
export function missingRequiredFields(
  data: Record<string, unknown>,
  category: string,
): RequiredField[] {
  return requiredFieldsFor(category).filter(rf => {
    const v = data[rf.field];
    return v == null || String(v).trim() === '';
  });
}

/**
 * Due profili appartengono alla stessa misura?
 *
 * Confronto diretto con tolleranza, NON tramite bucket: le larghezze fuori
 * elenco devono restare distinte fra loro.
 */
export function widthsMatch(
  a: number | null | undefined,
  b: number | null | undefined,
  tolerance = WIDTH_TOLERANCE_MM,
): boolean {
  const wa = Number(a);
  const wb = Number(b);
  if (!Number.isFinite(wa) || !Number.isFinite(wb)) return false;
  return Math.abs(wa - wb) <= tolerance;
}
